import { Flex } from '@chakra-ui/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ItemWrapper,
  UnreadWrapper,
} from '~/components/compositional/ListViewStyles'
import { FilterMenu } from '~/components/core'
import {
  DropdownSelect,
  SelectItem,
} from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'

import {
  type FilterExtension,
  type FilterStatus,
  statusFilterOptions,
  useFaxViewModel,
} from './Fax.viewmodel'
import { FaxDirectionFilterMenu } from './FaxDirectionFilter'

function ExtensionFilter() {
  const { t } = useTranslation()
  const {
    actions: { getMoreExtensions, setExtensionFilter },
    state: { extensions, extensionFilter, unreadCount },
  } = useFaxViewModel()

  const extensionOptions = useMemo<Array<SelectItem<FilterExtension>>>(
    () => [
      {
        content: (
          <MenuListItem primaryText={t('faxFilters.allFaxes', 'All Faxes')} />
        ),
        value: 'all',
      },
      ...extensions.map((extension) => {
        const count = unreadCount.byExtension.get(extension.id) ?? 0
        const unreadIndicator =
          count > 0 ? <UnreadWrapper>{count}</UnreadWrapper> : undefined

        return {
          content: (
            <MenuListItem
              primaryText={extension.name}
              secondaryText={extension.number}
              rightContent={unreadIndicator}
            />
          ),
          selectedLabel: extension.name,
          value: extension.id,
        }
      }),
    ],
    [extensions, t],
  )

  /** The `All Extensions` option is first, offsetting the index of selected extensions by 1. */
  const selectedExtensionIndex = useMemo(
    () =>
      extensionFilter === 'all'
        ? 0
        : extensions.findIndex(
            (extension) => extension.id === extensionFilter,
          ) + 1,
    [extensions, extensionFilter],
  )

  function selectExtension(value: FilterExtension) {
    setExtensionFilter(value)
  }

  return (
    <DropdownSelect
      placeholder={t('form.placeholder.selectVirtualFax')}
      items={extensionOptions}
      selectedIndex={selectedExtensionIndex}
      onScrollEndReached={getMoreExtensions}
      onSelect={selectExtension}
    />
  )
}

/** Determines which filters to render and how to arrange them */
function Filters() {
  const {
    state: { extensions },
  } = useFaxViewModel()

  // Hide the extension filter and show the remaining filters on a single row
  if (extensions.length <= 1) {
    return (
      <Flex gap="0.75rem">
        <StatusFilter />
        <FaxDirectionFilterMenu.Button />
      </Flex>
    )
  }

  // Show all filters on two rows; extension filter on top row, remaining filters on bottom row
  return (
    <>
      <Flex gap="0.75rem">
        <ExtensionFilter />
        <FaxDirectionFilterMenu.Button />
      </Flex>
      <StatusFilter />
    </>
  )
}

function Main() {
  return (
    <ItemWrapper style={{ height: 'fit-content' }} $bottom>
      <Flex flexDirection="column" gap="0.75rem" width="100%">
        <Filters />
      </Flex>
    </ItemWrapper>
  )
}

function StatusFilter() {
  const { t } = useTranslation()
  const {
    actions: { setStatusFilter },
    state: { statusFilter },
  } = useFaxViewModel()

  function changeUnreadFilter(selected: FilterStatus) {
    /** Always set to `all` when deselecting */
    const next = statusFilter === selected ? 'all' : selected
    setStatusFilter(next)
  }

  return (
    <FilterMenu
      selectedFilter={statusFilter}
      options={statusFilterOptions()}
      ariaLabel={t('a11y.filterGroup.fax')}
      onChange={changeUnreadFilter}
    />
  )
}

const FaxListFilters = Main

export default FaxListFilters
